/** @jsxImportSource @emotion/react */
/** @jsxFrag */

import React, { memo, useState, lazy, Suspense } from "react";
import { useIntl } from "react-intl";
import { useLocation, useHistory } from "react-router-dom";

import LanguageIcon from "@material-ui/icons/Language";

import { Drawer, ViewState } from "../..";
import { useAccount } from "../../../context/account";
import { useFeatureToggle } from "../../../context/featureToggle";
import { useToast } from "../../../context/toast";
import { useUser } from "../../../context/user";
import { authFetcher } from "../../../hooks/useAuthSWR";
import useHyperlinksByLocale from "../../../hooks/useHyperlinksByLocale";
import useUpgradePlan from "../../../hooks/useUpgradePlan";
import ActionMenus from "../../../pages/LiveEvents/ActionMenus";
import { RoutesProps, Routes } from "../../../routes";
import { routes as routesStyle } from "../../../styles/layout";
import { Tier } from "../../../types";
import { ItemType } from "../../Drawer";

const LandingModal = lazy(() => import("../../../components/Landing"));

const MainLayout = ({ routes }: RoutesProps): JSX.Element => {
  const intl = useIntl();
  const location = useLocation();
  const { push } = useHistory();
  const showToast = useToast();
  const { SUPPORT_URL } = useHyperlinksByLocale();
  const { hasFinishedLanding, locale, setLocale } = useUser();
  const { tier } = useAccount();
  const { requestInfo, popupUpgradeModal } = useUpgradePlan();
  const { isFeatureEnabled } = useFeatureToggle();
  const [loading, setLoading] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [shouldShowLanding, setShouldShowLanding] = useState<boolean>(!hasFinishedLanding);

  const { pathname } = location;

  const isUpToEventsLimit = async () => {
    try {
      const data: any = await authFetcher("/live-events?limit=10&page=1");
      return data?.data?.length >= 5;
    } catch (error) {
      return false;
    }
  };

  const handleClickCreateEvent = async (el: HTMLElement | null) => {
    try {
      const { shouldUpgrade, isEnterprise, isFreetrial } = await requestInfo();
      if (isEnterprise) {
        setAnchorEl(el);
        return;
      }
      if (shouldUpgrade) {
        popupUpgradeModal();
        return;
      }
      if (isFreetrial) {
        setLoading(true);
        const upToLimit = await isUpToEventsLimit();
        setLoading(false);
        if (upToLimit) {
          push("/pricing", { from: pathname });
          return;
        }
      }
      push("/live/new-event", { from: pathname });
    } catch (error) {
      console.error("> Live > handleClickCreateEvent: ", error);
      showToast(intl.formatMessage({ id: "general.error" }), {
        type: "error",
      });
    }
  };

  return (
    <>
      <Drawer
        items={[
          {
            icon: "add",
            txt: intl.formatMessage({ id: "live.drawer.create-event" }),
            highlight: true,
            onClick: (event: React.MouseEvent<HTMLElement>) => {
              event.preventDefault();
              handleClickCreateEvent(event.currentTarget);
            },
            loading,
            disabled: tier !== Tier.ENTERPRISE && pathname === "/live/new-event",
          },
          {
            icon: "date",
            txt: intl.formatMessage({ id: "live.drawer.events" }),
            to: "/live/events",
          },
          {
            icon: "camera",
            txt: intl.formatMessage({ id: "live.drawer.control-room" }),
            to: "/live/control-rooms",
          },
          ...(tier === Tier.ENTERPRISE
            ? [
                {
                  icon: "media",
                  txt: intl.formatMessage({ id: "live.drawer.media" }),
                  to: "/live/media",
                },
              ]
            : []),
          ...(isFeatureEnabled("word-filter")
            ? [
                {
                  icon: "wordfilters",
                  txt: intl.formatMessage({ id: "live.drawer.word-filters" }),
                  to: "/live/wordfilters",
                },
              ]
            : []),
          ...(isFeatureEnabled("sticker")
            ? [{ icon: "stickers", txt: intl.formatMessage({ id: "live.drawer.stickers" }), to: "/live/stickers" }]
            : []),
          ...(isFeatureEnabled("live-to-file")
            ? [
                {
                  icon: "org",
                  txt: intl.formatMessage({ id: "live.drawer.org-setting" }),
                  to: "/org-setting",
                  startAtBottom: true,
                },
              ]
            : []),
          {
            icon: <LanguageIcon />,
            itemType: ItemType.DropdownSelect,
            value: locale,
            onChange: (e: any) => {
              setLocale(e.target.value);
            },
            options: [
              { value: "en", label: "English" },
              { value: "ja", label: "日本語" },
              { value: "zh", label: "繁體中文" },
            ],
            ...(!isFeatureEnabled("live-to-file") ? { startAtBottom: true } : {}),
          },
          {
            icon: "headset",
            txt: intl.formatMessage({ id: "live.drawer.support" }),
            onClick: (): void => {
              window.open(SUPPORT_URL, "_blank");
            },
          },
          {
            icon: "logout",
            txt: intl.formatMessage({ id: "live.drawer.logout" }),
            to: "/logout",
          },
        ]}
      />
      <ActionMenus id="event-menu" anchorEl={anchorEl} onClickMenu={() => setAnchorEl(null)} />
      <div css={routesStyle}>
        <Routes routes={routes} />
      </div>
      {shouldShowLanding && (
        <Suspense fallback={<ViewState state="loading-mask" />}>
          <LandingModal open={shouldShowLanding} onClose={() => setShouldShowLanding(false)} />
        </Suspense>
      )}
    </>
  );
};

export default memo(MainLayout);
